import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import AppShell from "@/components/AppShell";
import PublicShell from "@/components/PublicShell";
import CompBanner from "@/components/CompBanner";
import { Button } from "@/components/ui/button";
import { PawPrintIcon as PawPrint, BoneIcon as Bone, DropIcon as Drop, ConfettiIcon as Confetti, LightningIcon as Lightning, ScalesIcon as Scales, PillIcon as Pill, StethoscopeIcon as Stethoscope, GraduationCapIcon as GraduationCap, ShowerIcon as Shower, SparkleIcon as Sparkle, HeartIcon as Heart, ShieldCheckIcon as ShieldCheck, ShareIcon as Share, FilePdfIcon as FilePdf, CheckCircleIcon as CheckCircle, ArrowRightIcon as ArrowRight } from "@phosphor-icons/react";
import api from "@/lib/api";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils-app";
import { isNativeApp, webOrigin } from "@/lib/nativeBridge";
import NativePlanNotice from "@/components/NativePlanNotice";

const TEN_KINDS = [
  { icon: Bone, label: "Meals", blurb: "Brand, cups, slot, appetite, water" },
  { icon: PawPrint, label: "Walks", blurb: "Distance, duration, pace, weather" },
  { icon: Drop, label: "Potty", blurb: "Type, consistency, color — flag concerns early" },
  { icon: Confetti, label: "Play", blurb: "Fetch, tug, training — where and with whom" },
  { icon: Lightning, label: "Energy", blurb: "1–5 level + mood + sleep pattern" },
  { icon: Scales, label: "Weight", blurb: "Tracked trend over 30 days" },
  { icon: Pill, label: "Meds", blurb: "Name, dose, route, with food" },
  { icon: Stethoscope, label: "Symptoms", blurb: "Severity + smart vet warning" },
  { icon: GraduationCap, label: "Training", blurb: "Build your dog's resume" },
  { icon: Shower, label: "Grooming", blurb: "Bath, brush, nails — cadence" },
];

/** Public-facing landing for the Dog Log subscription. Designed
 *  warm-emotional: dog imagery, soft cream/gold palette, friendly
 *  copy that speaks to owners (not breeders). */
export default function DogLogLanding() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    if (user) api.get("/care/status").then((r) => setStatus(r.data)).catch(() => {});
  }, [user]);

  const start = async (packageId) => {
    if (!user) {
      navigate("/login?intent=dog-log");
      return;
    }
    setBusy(packageId);
    try {
      const { data } = await api.post("/billing/checkout", {
        package_id: packageId,
        origin_url: webOrigin(),
      });
      if (data.url) window.location.href = data.url;
      else if (data.free) {
        toast.success("Dog Log unlocked — opening your dogs…");
        navigate("/dogs");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(null);
    }
  };

  const Shell = user ? AppShell : PublicShell;
  const monthly = status?.packages?.monthly?.amount ?? 9.99;
  const annual = status?.packages?.annual?.amount ?? 69;

  // Native app: this is a subscription pricing/checkout page —
  // App Review 3.1.1 forbids it in the shell. Neutral notice instead.
  if (isNativeApp()) return <NativePlanNotice />;

  return (
    <Shell>
      <div data-testid="dog-log-landing">
        {/* HERO */}
        <section className="relative overflow-hidden rounded-2xl border border-[#E8D9A6] bg-gradient-to-br from-[#FBF7EC] to-[#FFFDF6] p-8 sm:p-12 mb-10">
          <div className="grid grid-cols-1 md:grid-cols-[1.2fr_1fr] gap-10 items-center">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.32em] text-gold mb-3 flex items-center gap-1.5">
                <Sparkle size={11} weight="fill" /> Dog Log · for owners who notice everything
              </p>
              <h1 className="font-spectral text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] mb-5">
                Every meal.<br />
                Every walk.<br />
                <span className="text-crimson">Every milestone.</span>
              </h1>
              <p className="text-[#3F3F3F] text-lg leading-relaxed max-w-lg mb-6">
                The diary your dog deserves — and your vet will love. Ten one-tap
                categories, smart symptom alerts, sitter-ready reports, and a
                Saturday recap email that turns the week into a story.
              </p>
              {/* Comp on file — auto-hides for users without a comp. */}
              {user && <div className="mb-4 max-w-md"><CompBanner /></div>}
              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={() => start("care_plus_annual")}
                  disabled={!!busy}
                  className="rounded-full bg-ink hover:bg-crimson text-white px-6 h-12"
                  data-testid="dog-log-cta-annual"
                >
                  Get Dog Log — ${annual}/yr
                  <ArrowRight size={14} weight="bold" className="ml-2" />
                </Button>
                <Button
                  onClick={() => start("care_plus_monthly")}
                  disabled={!!busy}
                  variant="outline"
                  className="rounded-full border-ink h-12"
                  data-testid="dog-log-cta-monthly"
                >
                  Try monthly — ${monthly}/mo
                </Button>
              </div>
              <p className="text-[11px] text-[#6B6B6B] mt-3">
                Free with Breeder Pro &amp; Kennel Elite. Cancel anytime.
              </p>
            </div>
            <div className="relative hidden md:block">
              {/* Dog hero — phosphor PawPrint XL with a gradient halo */}
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_#FBE9A610_0%,_transparent_70%)]"></div>
              <div className="relative bg-white rounded-2xl border border-[#E8D9A6] p-6 shadow-[0_30px_60px_-20px_rgba(199,162,39,0.25)]">
                <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#6B6B6B] mb-3">Today with Buddy</p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2"><Bone size={14} weight="duotone" className="text-gold" /> Breakfast · 2 cups Acana · great appetite</li>
                  <li className="flex items-center gap-2"><PawPrint size={14} weight="duotone" className="text-[#3F4A8A]" /> 1.4 mi · brisk pace · sunny</li>
                  <li className="flex items-center gap-2"><Confetti size={14} weight="duotone" className="text-crimson" /> Tug in the yard · 12 min</li>
                  <li className="flex items-center gap-2"><Scales size={14} weight="duotone" className="text-ink" /> 42.5 lbs <span className="text-[#0F8043] text-[10px] font-bold ml-1">−0.3 lbs · 30d</span></li>
                  <li className="flex items-center gap-2"><Lightning size={14} weight="duotone" className="text-[#8A6A00]" /> Energy 4/5 · happy</li>
                </ul>
                <div className="mt-4 pt-3 border-t border-[#EAEAEA] flex items-center gap-2 text-[11px] text-[#6B6B6B]">
                  <Heart size={11} weight="fill" className="text-crimson" />
                  7-day walk streak · 8.2 mi this week
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 10 CATEGORIES */}
        <section className="mb-10">
          <h2 className="font-spectral text-3xl tracking-tight mb-2">10 categories, one tap each</h2>
          <p className="text-[#6B6B6B] mb-6">Designed by dog owners. Verified against vet intake forms.</p>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {TEN_KINDS.map((k) => (
              <div key={k.label} className="rounded-xl border border-[#EAEAEA] bg-white p-4 hover:border-gold/60 transition-colors">
                <k.icon size={22} weight="duotone" className="text-gold mb-2" />
                <p className="text-sm font-semibold">{k.label}</p>
                <p className="text-[11px] text-[#6B6B6B] leading-snug mt-1">{k.blurb}</p>
              </div>
            ))}
          </div>
        </section>

        {/* FEATURE TRIO */}
        <section className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
          <FeatureCard
            icon={Stethoscope}
            color="#C1272D"
            title="Smart vet warnings"
            blurb="Two days of symptoms at severity 3+ and Dog Log gently nudges you toward a vet visit. Stop wondering if you should worry."
          />
          <FeatureCard
            icon={FilePdf}
            color="#0F8043"
            title="Vet-ready PDFs"
            blurb="Three branded reports — Vet Visit, Sitter Briefing, Sitter Return — generated from your log. Print, email, or hand over."
          />
          <FeatureCard
            icon={Share}
            color="#3F4A8A"
            title="Sitter sharing, finally simple"
            blurb="One tap to share by SMS, WhatsApp, AirDrop, email, or QR code. Your sitter scans, logs, you see it live. No accounts."
          />
        </section>

        {/* TRUST STRIP */}
        <section className="rounded-2xl bg-ink text-white p-8 mb-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-start gap-3">
              <ShieldCheck size={22} weight="duotone" className="text-gold flex-shrink-0" />
              <div>
                <p className="font-semibold mb-1">Your data, your dog</p>
                <p className="text-[12px] text-white/70 leading-relaxed">Private to you. Sitters only see what you share, and only when active.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Heart size={22} weight="duotone" className="text-gold flex-shrink-0" />
              <div>
                <p className="font-semibold mb-1">Unlimited dogs</p>
                <p className="text-[12px] text-white/70 leading-relaxed">One subscription covers every dog you own. No per-dog fees, ever.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Sparkle size={22} weight="duotone" className="text-gold flex-shrink-0" />
              <div>
                <p className="font-semibold mb-1">Built into the Registry</p>
                <p className="text-[12px] text-white/70 leading-relaxed">Already with us as a breeder? Dog Log is included free with Pro and Elite plans.</p>
              </div>
            </div>
          </div>
        </section>

        {/* PRICING */}
        <section className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
          <PricingCard
            tier="Monthly"
            price={monthly}
            cadence="/mo"
            cta="Start monthly"
            highlight={false}
            packageId="care_plus_monthly"
            onStart={start}
            busy={busy}
            features={["All 10 log categories", "Smart vet warnings", "3 PDF reports", "Sitter sharing", "Saturday recap email", "Cancel anytime"]}
            testId="dog-log-pricing-monthly"
          />
          <PricingCard
            tier="Annual"
            price={annual}
            cadence="/yr"
            badge="Save 38%"
            cta="Start annual"
            highlight={true}
            packageId="care_plus_annual"
            onStart={start}
            busy={busy}
            features={["Everything in monthly", "$24.88 less per year", "Best for daily users", "Pause anytime"]}
            testId="dog-log-pricing-annual"
          />
        </section>

        {/* FAQ */}
        <section className="mb-12">
          <h2 className="font-spectral text-3xl tracking-tight mb-5">Common questions</h2>
          <div className="space-y-3">
            <Faq q="Do I need to be a breeder to use Dog Log?" a="No — Dog Log is for any dog owner. The rest of Armstrong K9 Registry leans toward breeders and buyers, but Dog Log was built specifically for owners who want to track their pet's daily life." />
            <Faq q="What if I already pay for Breeder Pro or Kennel Elite?" a="Dog Log is included free — no separate charge. You'll see the Dog Log tab on every dog profile automatically." />
            <Faq q="Can I cancel?" a="Anytime. Monthly stops at the end of the current billing month; annual gets pro-rated. Your log entries stay accessible forever — we never lock you out of your own data." />
            <Faq q="How does the sitter sharing work?" a="From any dog's Dog Log tab, hit 'New share link'. You set the dates and add care notes. Then send the link by SMS, WhatsApp, email, or QR code — your sitter opens it on their phone with no signup. They log activities in real time and you see everything live." />
            <Faq q="Is my dog's data private?" a="Yes. Only you can see your Dog Log. Sitters only see what you explicitly share during their active window. We never sell data, period." />
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="text-center py-10 mb-6 border-t border-[#EAEAEA]">
          <h2 className="font-spectral text-3xl tracking-tight mb-3">Start logging today.</h2>
          <p className="text-[#6B6B6B] mb-6">Your future self (and your vet) will thank you.</p>
          <Button
            onClick={() => start("care_plus_annual")}
            disabled={!!busy}
            className="rounded-full bg-ink hover:bg-crimson text-white px-8 h-12"
            data-testid="dog-log-cta-final"
          >
            Get Dog Log — ${annual}/yr <ArrowRight size={14} weight="bold" className="ml-2" />
          </Button>
        </section>
      </div>
    </Shell>
  );
}

function FeatureCard({ icon: Icon, color, title, blurb }) {
  return (
    <div className="rounded-xl border border-[#EAEAEA] bg-white p-5">
      <div className="h-10 w-10 rounded-lg flex items-center justify-center mb-3" style={{ background: `${color}15` }}>
        <Icon size={20} weight="duotone" style={{ color }} />
      </div>
      <p className="font-semibold mb-1">{title}</p>
      <p className="text-[13px] text-[#6B6B6B] leading-relaxed">{blurb}</p>
    </div>
  );
}

function PricingCard({ tier, price, cadence, badge, cta, highlight, packageId, onStart, busy, features, testId }) {
  return (
    <div
      className={`rounded-2xl p-6 ${highlight ? "bg-ink text-white" : "bg-white border border-[#EAEAEA]"}`}
      data-testid={testId}
    >
      <div className="flex items-center justify-between mb-2">
        <p className={`text-[10px] uppercase tracking-[0.22em] font-bold ${highlight ? "text-gold" : "text-[#6B6B6B]"}`}>{tier}</p>
        {badge && (
          <span className="text-[10px] uppercase tracking-[0.18em] font-bold bg-gold text-ink rounded-full px-2 py-0.5">{badge}</span>
        )}
      </div>
      <div className={`font-spectral font-light text-5xl tracking-tight mb-1 ${highlight ? "text-white" : "text-ink"}`}>
        ${price}<span className={`text-base font-normal ml-1 ${highlight ? "text-white/70" : "text-[#9A9A9A]"}`}>{cadence}</span>
      </div>
      <ul className="space-y-1.5 mt-4 mb-5">
        {features.map((f) => (
          <li key={f} className="flex items-center gap-2 text-sm">
            <CheckCircle size={12} weight="fill" className={highlight ? "text-gold" : "text-[#0F8043]"} />
            <span className={highlight ? "text-white/85" : "text-[#3F3F3F]"}>{f}</span>
          </li>
        ))}
      </ul>
      <Button
        onClick={() => onStart(packageId)}
        disabled={!!busy}
        className={`w-full rounded-full ${highlight ? "bg-white hover:bg-gold text-ink" : "bg-ink hover:bg-crimson text-white"}`}
      >
        {cta}
      </Button>
    </div>
  );
}

function Faq({ q, a }) {
  return (
    <details className="rounded-lg border border-[#EAEAEA] bg-white p-4 group">
      <summary className="font-semibold text-sm cursor-pointer flex items-center justify-between">
        {q}
        <ArrowRight size={14} weight="bold" className="text-[#9A9A9A] group-open:rotate-90 transition-transform" />
      </summary>
      <p className="text-[13px] text-[#3F3F3F] leading-relaxed mt-3">{a}</p>
    </details>
  );
}
